import { Inject, Injectable, OnInit } from '@angular/core';
import AppSettings from '../configurations/DefaultSettings.json';
import { StorageService } from './storage.service';
import { HttpClient } from '@angular/common/http';
import { v4 as uuidv4 } from 'uuid';
import { IClientSettings, IPIMSettingItem, ISmCategory } from '../interfaces';
import { DebugService } from '../services/debug.service';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { PIMSettingsMapperService } from './pim-settingsmapper.service';
import { pimSoftware } from '../enums';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {

  private environment: {[key: string]: string};
  private isGiftcardApp:boolean = false;

  settings:any;

  constructor(
    private storageService: StorageService,
    private http: HttpClient,
    private debug: DebugService,
    private pimSettingsMapperService: PIMSettingsMapperService,

    @Inject('environment')
    environment: {[key: string]: string}

  ) { 
    this.environment = environment;
  }

  onAppInit() {
    return new Promise<boolean>((resolve , reject) => {

      if (this.isDemoApp()) {       
        let settingsFromStorage = this.loadSettingsFromStorage();
        if (settingsFromStorage) {
          this.settings = settingsFromStorage;
          resolve(true);
        } 
        else {
          this.loadSettingsFromApi().subscribe({
            next: (res:any) => {
              this.settings = res;
              this.storageService.saveSessionStorage('AppSettings', this.settings);
              resolve(true);
            },
            error: (e) => {
              resolve(false);
            }
          });
        }
      }
      else {
        this.loadSettingsFromApi().subscribe({
            next: (res:any) => {
              this.settings = res;
              resolve(true);
            },
            error: (e) => {
              resolve(false);
            },
          });
      }
    });
  }

  onAppInitGiftCard() {
    this.isGiftcardApp = true;
    return new Promise<boolean>((resolve , reject) => {
      this.loadGiftCardSettingsFromApi().subscribe({
        next: (res:any) => {
          if(res) {
            this.settings = res;

            resolve(true);
          }
          else {
            this.debug.log('INIT ERROR: No gift card settings received from API');
            resolve(false);
          }
          
        },
        error: (e) => {
          this.debug.log('INIT ERROR: Error loading gift card settings from API', e);
          resolve(false);
        }
      });
    });
  }

  loadSettingsFromApi(): Observable<IClientSettings> {
    const headers = { 'X-Conversation-ID': uuidv4() };

    const pimSettings$ = this.http
      .get<IPIMSettingItem[]>(
        `${this.environment.backendHost}/api/Content/json/ApiClientSettingsFrontend`,
        { responseType: 'json', headers }
      )
      .pipe(
        map(pimSettings =>
          this.filterPIMSettingsBySoftware(pimSettings, pimSoftware.BOOKINGENGINE)
        ),
        catchError(err => {
          console.warn('PIM settings not available, continuing with old settings', err);
          return of(null); // 👈 important
        })
      );

    const oldSettings$ = this.http.get<IClientSettings>(
      `${this.environment.backendHost}/api/Content/json/ClientSettings`,
      { responseType: 'json', headers }
    );

    return forkJoin({
      newSettings: pimSettings$,
      oldSettings: oldSettings$
    }).pipe(
      map(({ newSettings, oldSettings }) => {
        this.debug.log('SettingsService: Loaded settings from API', {
          newSettings,
          oldSettings
        });

        if (newSettings && newSettings.length > 0) {
          const mergedSettings =
            this.pimSettingsMapperService.mergeSettings(oldSettings, newSettings);

          this.debug.log(
            'SettingsService: New settings found. Merged final settings',
            mergedSettings
          );

          return mergedSettings;
        }

        this.debug.log(
          'SettingsService: No new settings, using old settings',
          oldSettings
        );

        return oldSettings;
      })
    );
  }

  loadGiftCardSettingsFromApi() {

    const headers = { 'X-Conversation-ID': uuidv4() };

    const pimSettings$ = this.http
      .get<IPIMSettingItem[]>(
        `${this.environment.backendHost}/api/Content/json/ApiClientSettingsFrontend`,
        { responseType: 'json', headers }
      )
      .pipe(
        map((pimSettings) => {
          if (!pimSettings || pimSettings.length === 0) {
            return null;
          }
          return this.filterPIMSettingsBySoftware(pimSettings, pimSoftware.GIFTCARD);
        }),
        catchError(err => {
          console.warn('PIM settings not available, continuing with old settings', err);
          return of(null); // 👈 important
        })
      );

    const oldSettings$ = this.http.get<IClientSettings>(
      `${this.environment.backendHost}/api/Content/json/GiftCardClientSettings`,
      { responseType: 'json', headers }
    );

    return forkJoin({ newSettings: pimSettings$, oldSettings: oldSettings$ }).pipe(
      
      map(({ newSettings, oldSettings }) => {
        if (newSettings) {
          const mergedSettings = this.pimSettingsMapperService.mergeSettings(oldSettings, newSettings);
          this.debug.log('SettingsService: New settings found. Merged final settings', mergedSettings);
          return mergedSettings;
        }
        else if (oldSettings) {
          this.debug.log('SettingsService: No new settings, using old settings', oldSettings);
          return oldSettings;
        }
        else {
          console.error('SettingsService: Failed to load settings from API');
          throw new Error('Failed to load settings from API');
        }
      })
    );
  }

  filterPIMSettingsBySoftware(pimSettings:IPIMSettingItem[], pimSoftware: pimSoftware) {
    return pimSettings.filter(setting => setting.settingName.software === pimSoftware);
  }
  
  get(value:any):any {
    let settings = this.settings;
    if (this.isDemoApp()) {
      settings = this.loadSettingsFromStorage();
    }
    if (!settings) {
      return undefined;
    }
    return settings[value];
  }

  set(key:string, value:any):void {
    let settings = this.settings;
    if (this.isDemoApp()) {
      settings = this.loadSettingsFromStorage();
    }
    settings[key] = value;
    this.storageService.saveSessionStorage('AppSettings', settings);
  }

  setAndReload(key:string, value:any):void {
    this.set(key, value);
    document.location.reload();
  }

  /**
   * Whether our own cookie consent banner may be shown. Only an explicit false from PIM turns it off
   * (consent is then assumed to be given by other means, e.g. the customer's own script).
   */
  askForCookieConsent():boolean {
    const key = this.isGiftcardApp ? 'app.giftcardAskForCookieConsent' : 'app.askForCookieConsent';
    return this.get(key) !== false;
  }

  isDemoApp():boolean {
    if (this.isGiftcardApp) {
      return false;
    }
    let hostname = window.location.href;
    return(
      hostname.includes('localhost') || 
      hostname.includes('cloud.hotellinx.com/sampo') ||
      hostname.includes('varausdemo.hotellinx.com') ||
      hostname.includes('frontend-dev-wbe.hotellinx.com')
    ); 
  }


  getCategories(language:string):ISmCategory[]|undefined {
    let categories = this.get("searchMask.categories");
    
    let cats;
    if (categories) {
      cats = [];
      categories.forEach((category:any) => {
        let foundCategoryName = category.name.find((item:any) => item.lang == language);
        let c:any = {
          value: category.value,
          name: foundCategoryName?.text ?? 'No translation'
        };
       
        cats.push(c);
      });
    }
    return cats;
  }

  getEndpoint(hotelCode?:string) {

     // endpoint aina sama, ei oteta clientSettings.jsonista
    return this.environment.backendHost+'/api';
    
    /*
    try {

       if (this.environment.isDev) {
        return this.environment.backendHost+'/api';
      }
      let endpointConfigs = this.get('app.endpoints');
      let config
      if (hotelCode) {
        config = endpointConfigs.find((c:any) => {return c.hotelCode == hotelCode});
      }
      else {
        config = endpointConfigs[0];
      }
      if (config?.endpoint) {
        return config.endpoint 
      }
      return null;
    }
    catch(e) {
      console.error('Get endpoint:', e);
    } */
    
  }

  getPaymentEndpoint() {
    return this.environment.paymentEndpoint+'/api';
  }

  getApiAuthentication() {
    return {
      apiKey: this.environment.apiKey,
      tenantId: this.environment.tenantId ?? "",
      software: this.environment.software ?? 6,
    }
  }
 

  loadSettingsFromStorage() {
    let settings = this.storageService.loadSessionStorage('AppSettings');
    if (!settings) {
      return;
    }
    else  {
      return settings;
    }
    
  }


  
}
